import { useState, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import { X, Trash2, ChevronLeft, BarChart3, Layers, Map as MapIcon, ArrowRight } from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useResourceDetailSidebarResize } from '../hooks/useDetailSidebarResize';
import { useResourceForm } from '../../resources/useResourceForm';
import { useResourceBoardStats } from '../hooks/useResourceBoardStats';
import { ScopeToggle, ColorPicker, FieldsList } from '../../resources/components/ResourceFormFields';
import { ResourceCanvasOverviewPanel } from './ResourceCanvasOverviewPanel';
import { formatAggregatedValue } from '../../lib/formatFieldValue';

// Outer shell
export const ResourceDetailSidebar = memo(function ResourceDetailSidebar() {
  const { t } = useTranslation();
  const { uiStore } = useCanvasStores();
  const resourceDetailResourceId = useSelector(uiStore, s => (s.context as any).resourceDetailResourceId);
  const resourceDetailWidth = useSelector(uiStore, s => (s.context as any).resourceDetailWidth);
  const isResourceDetailCollapsed = useSelector(uiStore, s => (s.context as any).isResourceDetailCollapsed);
  const isResourceDetailResizing = useSelector(uiStore, s => (s.context as any).isResourceDetailResizing);

  const { handleResizeStart, handleDoubleClick, toggleCollapse } = useResourceDetailSidebarResize(uiStore);

  if (resourceDetailResourceId === null) return null;

  if (isResourceDetailCollapsed) {
    return (
      <div
        style={{
          width: 24,
          flexShrink: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #e2e8f0',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: 32,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          onClick={toggleCollapse}
          title={t('resource.detailSidebar.expandSidebar')}
        >
          <ChevronLeft size={12} style={{ color: '#64748b' }} />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={toggleCollapse}
        >
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 11,
              fontWeight: 500,
              color: '#64748b',
              letterSpacing: '0.05em',
            }}
          >
            Resource
          </span>
        </div>

        {/* Resize Handle (left edge) */}
        <div
          onMouseDown={handleResizeStart}
          onDoubleClick={handleDoubleClick}
          style={{
            position: 'absolute',
            top: 0,
            left: -2,
            width: 4,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 31,
            backgroundColor: 'transparent',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: resourceDetailWidth,
        flexShrink: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #e2e8f0',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <ResourceDetailContent resourceId={resourceDetailResourceId} />

      {/* Resize Handle (left edge) */}
      <div
        onMouseDown={handleResizeStart}
        onDoubleClick={handleDoubleClick}
        style={{
          position: 'absolute',
          top: 0,
          left: -2,
          width: 4,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 31,
          backgroundColor: isResourceDetailResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isResourceDetailResizing) {
            e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isResourceDetailResizing) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      />
    </div>
  );
});

// Inner content
function ResourceDetailContent({ resourceId }: { resourceId: string }) {
  const { t } = useTranslation();
  const { uiStore } = useCanvasStores();
  const form = useResourceForm(resourceId);
  const boardStats = useResourceBoardStats(resourceId);
  const [overviewOpen, setOverviewOpen] = useState(false);

  useEffect(() => {
    if (!form.existingResource) {
      uiStore.send({ type: 'resourceDetail.close' } as any);
    }
  }, [form.existingResource, uiStore]);

  const handleClose = useCallback(() => {
    uiStore.send({ type: 'resourceDetail.close' } as any);
  }, [uiStore]);

  const handleDelete = useCallback(() => {
    if (form.deleteResource()) {
      uiStore.send({ type: 'resourceDetail.close' } as any);
    }
  }, [form, uiStore]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 shrink-0">
        <span className="text-xs font-medium text-gray-500">
          {t('resource.detailSidebar.title')}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOverviewOpen(true)}
            title={t('resource.detailSidebar.openOverview')}
            className="text-indigo-400 hover:text-indigo-600 p-0.5 rounded"
          >
            <BarChart3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Form content */}
      <div className="flex-1 overflow-y-auto px-3 py-3" style={{ minHeight: 0 }}>
        {/* Name */}
        <div className="mb-3">
          <label className="block text-[10px] font-medium text-gray-500 mb-1">{t('resource.name')}</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => form.setName(e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
            autoFocus
          />
        </div>

        {/* Category */}
        <div className="mb-3">
          <label className="block text-[10px] font-medium text-gray-500 mb-1">{t('resource.category')}</label>
          <input
            type="text"
            value={form.category}
            onChange={(e) => form.setCategory(e.target.value)}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
          />
        </div>

        <ScopeToggle scopeType={form.scopeType} onChange={form.setScopeType} />
        <ColorPicker color={form.color} onChange={form.setColor} />
        <FieldsList
          fields={form.fields}
          onAddField={form.addField}
          onUpdateField={form.updateField}
          onRemoveField={form.removeField}
        />

        {/* Board-wide usage stats */}
        <ResourceBoardStatsSection stats={boardStats} onOpenOverview={() => setOverviewOpen(true)} />
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {form.loadedResourceId && (
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              {t('common.delete')}
            </button>
          )}
        </div>
        <span className="text-[10px] text-gray-400">ESC</span>
      </div>

      {/* Canvas Overview Panel */}
      <ResourceCanvasOverviewPanel isOpen={overviewOpen} onClose={() => setOverviewOpen(false)} />
    </>
  );
}

// Board-wide stats section in detail sidebar
function ResourceBoardStatsSection({ stats, onOpenOverview }: {
  stats: import('../hooks/useResourceBoardStats').ResourceBoardStats;
  onOpenOverview: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
      {/* Section header */}
      <div style={{ fontSize: 10, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
        <Layers size={10} style={{ color: '#6366f1' }} />
        {t('resource.detailSidebar.boardStats')}
      </div>

      {stats.totalEntityCount === 0 ? (
        <div style={{ fontSize: 10, color: '#94a3b8', padding: '8px 0' }}>
          <div>{t('resource.detailSidebar.notUsed')}</div>
          <div style={{ marginTop: 2, fontSize: 9 }}>{t('resource.detailSidebar.notUsedHint')}</div>
        </div>
      ) : (
        <>
          {/* Summary chips */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <div style={{
              flex: 1, padding: '5px 8px', borderRadius: 4,
              backgroundColor: '#f0f9ff', border: '1px solid #e0f2fe',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1' }}>{stats.totalEntityCount}</div>
              <div style={{ fontSize: 9, color: '#6b7280' }}>{t('resource.detailSidebar.attachments')}</div>
            </div>
            <div style={{
              flex: 1, padding: '5px 8px', borderRadius: 4,
              backgroundColor: '#faf5ff', border: '1px solid #f3e8ff',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>{stats.totalCanvasCount}</div>
              <div style={{ fontSize: 9, color: '#6b7280' }}>{t('resource.detailSidebar.canvasLabel')}</div>
            </div>
          </div>

          {/* Aggregated field values */}
          {stats.aggregatedFields.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                {t('resource.detailSidebar.aggregatedValues')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {stats.aggregatedFields.map(field => (
                  <div
                    key={field.fieldId}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '4px 6px', borderRadius: 4,
                      backgroundColor: '#f8fafc', border: '1px solid #f1f5f9',
                    }}
                  >
                    <span style={{ fontSize: 10, color: '#6b7280' }}>{field.key}</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                        {formatAggregatedValue({ type: field.type, value: field.totalValue, unit: field.unit, interval: field.interval })}
                      </span>
                      {field.attachmentCount > 1 && field.totalValue !== field.singleValue && (
                        <span style={{ fontSize: 9, color: '#94a3b8' }}>
                          ({field.attachmentCount} &times; {formatAggregatedValue({ type: field.type, value: field.singleValue, unit: field.unit, interval: field.interval })})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-canvas breakdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {stats.canvasUsages.map(usage => (
              <div
                key={usage.canvasName}
                style={{
                  padding: '4px 6px', borderRadius: 4,
                  backgroundColor: '#f8fafc', border: '1px solid #f1f5f9',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MapIcon size={9} style={{ color: '#94a3b8' }} />
                    <span style={{ fontSize: 10, fontWeight: 500, color: '#374151' }}>{usage.canvasName}</span>
                  </div>
                  <span style={{ fontSize: 9, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                    {usage.entityCount}x
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {usage.entityNames.map((name, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 9, padding: '0px 4px', borderRadius: 2,
                        backgroundColor: '#e2e8f0', color: '#475569',
                      }}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Overview link — always visible */}
      <button
        onClick={onOpenOverview}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', marginTop: 10, padding: '6px 8px', borderRadius: 4,
          backgroundColor: '#eef2ff', border: '1px solid #e0e7ff',
          cursor: 'pointer', transition: 'background-color 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#e0e7ff')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#eef2ff')}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChart3 size={12} style={{ color: '#6366f1' }} />
          <span style={{ fontSize: 10, fontWeight: 500, color: '#4338ca' }}>
            {t('resource.detailSidebar.openOverview')}
          </span>
        </div>
        <ArrowRight size={10} style={{ color: '#6366f1' }} />
      </button>
    </div>
  );
}

